import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  LinearProgress,
  DialogContentText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  AssignmentLate as LateIcon,
  NoAccounts as NoShowIcon,
  ExitToApp as EarlyLeaveIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatScheduledDate } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

const AbsenceTracking = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = ['admin', 'superadmin'].includes(user?.user_type);
  // TKT-463: delete test records (with a confirmation).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('list'); // 'list', 'analytics'
  
  // Data states
  const [absences, setAbsences] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    employee_id: '',
    absence_type_id: '',
    absence_date: '',
    hours_missed: 0,
    reason: '',
    is_excused: false
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    type: 'all',
    employee: 'all',
    status: 'all',
    date_range: 'all'
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAbsences(),
        loadAbsenceTypes(),
        loadEmployees(),
        loadStats()
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAbsences = async () => {
    try {
      const response = await makeApiCall('api/absences');
      if (response.ok) {
        const data = await response.json();
        setAbsences(data.absences || []);
      } else {
        // TKT-203: only fall back to mock data when the backend
        // endpoint genuinely is not present (404). For real errors
        // (500/auth) surface the failure so the office doesn't see
        // "created successfully" while the list reverts to mock rows
        // that hide the newly created record.
        if (response.status === 404) {
          setAbsences(getMockAbsences());
        } else {
          setAbsences([]);
          const data = await response.json().catch(() => ({}));
          setError(data.error || 'Failed to load absence records');
        }
      }
    } catch (err) {
      console.error('Error loading absences:', err);
      setError(err.message || 'Failed to load absence records');
      setAbsences([]);
    }
  };

  const loadAbsenceTypes = async () => {
    try {
      const response = await makeApiCall('api/absences/types');
      if (response.ok) {
        const data = await response.json();
        setAbsenceTypes(data.absence_types || []);
      } else {
        setAbsenceTypes([]);
      }
    } catch (err) {
      console.error('Error loading absence types:', err);
      setAbsenceTypes([]);
    }
  };

  const loadEmployees = async () => {
    try {
      // TKT-463: /api/employees returns 10 rows unless asked for more, so
      // only the first 10 staff appeared in the Employee list.
      const response = await makeApiCall('api/employees?page=1&limit=1000');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  // TKT-463 follow-up: only employees explicitly marked active count as
  // staff (old test rows have no flag). Both Employee lists use this, so an
  // inactive person like Aiden only appears where a record still needs them.
  const sortedEmployees = (list) => list
    .slice()
    .sort((a, b) => String(a.employee_name || '').localeCompare(String(b.employee_name || '')));

  const employeeOptions = (keepIds) => sortedEmployees(
    employees.filter((emp) => emp.is_active === true || keepIds.has(String(emp.id)))
  );

  const loadStats = async () => {
    try {
      const response = await makeApiCall('api/absences/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || {});
      } else {
        setStats({});
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      setStats({});
    }
  };

  const getMockAbsences = () => [
    {
      id: 1,
      employee_id: 1,
      employee_name: 'John Doe',
      absence_type_id: 1,
      absence_type_name: 'Late Arrival',
      absence_date: '2025-10-15',
      hours_missed: 1.5,
      reason: 'Traffic jam on highway',
      is_excused: false,
      points_assessed: 1,
      created_at: '2025-10-15T09:30:00Z'
    },
    {
      id: 2,
      employee_id: 2,
      employee_name: 'Jane Smith',
      absence_type_id: 2,
      absence_type_name: 'No Show',
      absence_date: '2025-10-14',
      hours_missed: 8,
      reason: 'No call, no show',
      is_excused: false,
      points_assessed: 3,
      created_at: '2025-10-14T10:00:00Z'
    },
    {
      id: 3,
      employee_id: 3,
      employee_name: 'Mike Johnson',
      absence_type_id: 3,
      absence_type_name: 'Early Departure',
      absence_date: '2025-10-13',
      hours_missed: 2,
      reason: 'Personal emergency',
      is_excused: true,
      points_assessed: 0,
      created_at: '2025-10-13T15:00:00Z'
    },
    {
      id: 4,
      employee_id: 1,
      employee_name: 'John Doe',
      absence_type_id: 1,
      absence_type_name: 'Late Arrival',
      absence_date: '2025-10-10',
      hours_missed: 0.5,
      reason: 'Car trouble',
      is_excused: true,
      points_assessed: 0,
      created_at: '2025-10-10T08:30:00Z'
    }
  ];

  const getAbsenceIcon = (type) => {
    switch (type) {
      case 'Late Arrival': return <LateIcon />;
      case 'No Show': return <NoShowIcon />;
      case 'Early Departure': return <EarlyLeaveIcon />;
      default: return <WarningIcon />;
    }
  };

  const getAbsenceColor = (type) => {
    const absenceType = absenceTypes.find(t => t.absence_type_name === type);
    return absenceType?.color_code || '#757575';
  };

  const handleCreateAbsence = () => {
    setEditingAbsence(null);
    setFormData({
      employee_id: '',
      absence_type_id: '',
      absence_date: '',
      hours_missed: 0,
      reason: '',
      is_excused: false
    });
    setOpenDialog(true);
  };

  const handleEditAbsence = (absence) => {
    setEditingAbsence(absence);
    setFormData({
      employee_id: absence.employee_id,
      absence_type_id: absence.absence_type_id,
      absence_date: absence.absence_date,
      hours_missed: absence.hours_missed,
      reason: absence.reason,
      is_excused: absence.is_excused
    });
    setOpenDialog(true);
  };

  const handleViewAbsence = (absence) => {
    setSelectedAbsence(absence);
  };

  const handleSaveAbsence = async () => {
    try {
      const url = editingAbsence ? `api/absences/${editingAbsence.id}` : 'api/absences';
      const method = editingAbsence ? 'PUT' : 'POST';
      
      const hours = Number(formData.hours_missed);
      const response = await makeApiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, hours_missed: Number.isFinite(hours) ? hours : 0 })
      });
      
      if (response.ok) {
        setError('');
        setSuccess(editingAbsence ? 'Absence record updated successfully' : 'Absence record created successfully');
        setOpenDialog(false);
        loadAbsences();
        loadStats();
      } else {
        // Say why instead of a generic failure (TKT-463).
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || '');
      }
    } catch (err) {
      setSuccess('');
      setError(err.message
        ? t('absences.saveFailedReason', { reason: err.message })
        : t('absences.saveFailed'));
    }
  };

  const confirmDeleteAbsence = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await makeApiCall(`api/absences/${deleteTarget.id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || '');
      }
      setError('');
      setSuccess(t('absences.deleted'));
      setDeleteTarget(null);
      loadAbsences();
      loadStats();
    } catch (err) {
      setSuccess('');
      setError(t('absences.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const renderHeader = () => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Typography variant="h4" component="h1" color="primary" fontWeight="bold">
        Absence Tracking
      </Typography>
      {/* TKT-203: removed Analytics toggle until the analytics view is
          actually implemented; tapping it previously did nothing because
          the render only knows the List view. KPI cards above the list
          already provide the high-level summary. */}
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateAbsence}
        >
          Record Absence
        </Button>
      </Box>
    </Box>
  );

  const renderStats = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {stats.total_absences || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Absences
                </Typography>
              </Box>
              <WarningIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {stats.unexcused_absences || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Unexcused
                </Typography>
              </Box>
              <ErrorIcon color="error" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {stats.excused_absences || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Excused
                </Typography>
              </Box>
              <CheckIcon color="success" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.total_points_assessed || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Points Assessed
                </Typography>
              </Box>
              <TrendingUpIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Absence Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                label="Absence Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {absenceTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.absence_type_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={filters.employee}
                onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                label="Employee"
              >
                <MenuItem value="all">All Employees</MenuItem>
                {/* Inactive staff stay filterable while they have records. */}
                {employeeOptions(new Set([
                  ...absences.map((absence) => String(absence.employee_id)),
                  String(filters.employee),
                ])).map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="excused">Excused</MenuItem>
                <MenuItem value="unexcused">Unexcused</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderAbsencesList = () => {
    const filteredAbsences = absences.filter(absence => {
      if (filters.type !== 'all' && absence.absence_type_id !== parseInt(filters.type)) return false;
      if (filters.employee !== 'all' && absence.employee_id !== parseInt(filters.employee)) return false;
      if (filters.status !== 'all') {
        if (filters.status === 'excused' && !absence.is_excused) return false;
        if (filters.status === 'unexcused' && absence.is_excused) return false;
      }
      return true;
    });

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Absence Records
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Hours Missed</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAbsences
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((absence) => (
                    <TableRow key={absence.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {absence.employee_name.charAt(0)}
                          </Avatar>
                          {absence.employee_name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getAbsenceIcon(absence.absence_type_name)}
                          <Chip
                            label={absence.absence_type_name}
                            size="small"
                            sx={{ backgroundColor: getAbsenceColor(absence.absence_type_name), color: 'white' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{formatScheduledDate(absence.absence_date)}</TableCell>
                      <TableCell>{absence.hours_missed}h</TableCell>
                      <TableCell>
                        <Chip
                          label={absence.points_assessed}
                          color={absence.points_assessed > 0 ? 'error' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={absence.is_excused ? 'Excused' : 'Unexcused'}
                          color={absence.is_excused ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={absence.reason}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {absence.reason}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewAbsence(absence)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditAbsence(absence)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {isAdmin && (
                            <Tooltip title={t('absences.delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(absence)}
                                aria-label={t('absences.deleteAria', { name: absence.employee_name || '' })}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredAbsences.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    );
  };

  const renderViewDialog = () => {
    if (!selectedAbsence) return null;
    return (
      <Dialog
        open={Boolean(selectedAbsence)}
        onClose={() => setSelectedAbsence(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="absence-view-dialog-title"
      >
        <DialogTitle id="absence-view-dialog-title">
          Absence Record Details
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar>{selectedAbsence.employee_name?.charAt(0) || '?'}</Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {selectedAbsence.employee_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Recorded {selectedAbsence.created_at ? new Date(selectedAbsence.created_at).toLocaleString() : '-'}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={selectedAbsence.is_excused ? 'Excused' : 'Unexcused'}
                color={selectedAbsence.is_excused ? 'success' : 'error'}
              />
            </Box>
            <Divider />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Absence Type</Typography>
                <Box mt={0.5} display="flex" alignItems="center" gap={1}>
                  {getAbsenceIcon(selectedAbsence.absence_type_name)}
                  <Chip
                    label={selectedAbsence.absence_type_name || '-'}
                    size="small"
                    sx={{ backgroundColor: getAbsenceColor(selectedAbsence.absence_type_name), color: 'white' }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Date</Typography>
                <Typography variant="body1">
                  {selectedAbsence.absence_date ? formatScheduledDate(selectedAbsence.absence_date) : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Hours Missed</Typography>
                <Typography variant="body1">{selectedAbsence.hours_missed ?? 0}h</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Points Assessed</Typography>
                <Box mt={0.5}>
                  <Chip
                    label={selectedAbsence.points_assessed ?? 0}
                    color={(selectedAbsence.points_assessed ?? 0) > 0 ? 'error' : 'default'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption" color="textSecondary">Reason</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedAbsence.reason || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAbsence(null)} aria-label="Close absence details">
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              const absence = selectedAbsence;
              setSelectedAbsence(null);
              handleEditAbsence(absence);
            }}
            aria-label="Edit this absence record"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderAbsenceDialog = () => (
    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingAbsence ? 'Edit Absence Record' : 'Record New Absence'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                label="Employee"
              >
                {/* Active staff only for new records; keep the current
                    employee listed when editing an older record. */}
                {employeeOptions(new Set([String(formData.employee_id)]))
                  .map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.employee_name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Absence Type</InputLabel>
              <Select
                value={formData.absence_type_id}
                onChange={(e) => setFormData({ ...formData, absence_type_id: e.target.value })}
                label="Absence Type"
              >
                {absenceTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.absence_type_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Absence Date"
              type="date"
              value={formData.absence_date}
              onChange={(e) => setFormData({ ...formData, absence_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Hours Missed"
              type="number"
              value={formData.hours_missed}
              onChange={(e) => setFormData({ ...formData, hours_missed: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Reason"
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            />
          </Grid>
          <Grid size={12}>
            <FormControl>
              <Box display="flex" alignItems="center">
                <input
                  type="checkbox"
                  checked={formData.is_excused}
                  onChange={(e) => setFormData({ ...formData, is_excused: e.target.checked })}
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Mark as excused absence
                </Typography>
              </Box>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveAbsence}>
          {editingAbsence ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {renderHeader()}
      {renderStats()}
      {renderFilters()}
      {renderAbsencesList()}
      {renderAbsenceDialog()}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        aria-labelledby="delete-absence-title"
      >
        <DialogTitle id="delete-absence-title">{t('absences.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget && t('absences.deleteConfirm', {
              name: deleteTarget.employee_name || '',
              date: formatScheduledDate(deleteTarget.absence_date),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>{t('common.cancel')}</Button>
          <Button onClick={confirmDeleteAbsence} color="error" variant="contained" disabled={deleting}>
            {t('absences.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      {renderViewDialog()}
    </Box>
  );
};

export default AbsenceTracking;
